<template>
  <div class="two-factor-setup">

    <!-- Paso 1: confirmar el correo -->
    <div v-if="step === 'verify-email'">
      <template v-if="!emailCodeSent">
        <p class="two-factor-setup__label">
          Antes de elegir cómo recibir tus códigos, confirma que el correo
          <strong v-if="hint">{{ hint }}</strong><span v-else>de tu cuenta</span> es tuyo.
        </p>
        <vs-button
          class="w-full"
          color="primary"
          type="relief"
          :disabled="loading"
          @click="sendEmailCode">Enviarme el código</vs-button>
      </template>
      <template v-else>
        <p class="two-factor-setup__label">
          Te enviamos un código a <strong>{{ hint }}</strong>. Escríbelo aquí:
        </p>
        <two-factor-code-input ref="codeInput" :disabled="loading" @input="code = $event" @complete="verifyEmail"/>
        <p class="two-factor-setup__muted text-center mt-3">
          <span v-if="resendIn > 0">Puedes pedir otro código en {{ resendIn }} s</span>
          <a v-else href="#" @click.prevent="sendEmailCode">Reenviar código</a>
        </p>
        <vs-button
          class="w-full mt-5"
          color="primary"
          type="relief"
          :disabled="loading || code.length !== 6"
          @click="verifyEmail">Confirmar correo</vs-button>
      </template>
    </div>

    <!-- Paso 2: elegir método -->
    <div v-else-if="step === 'choose'">
      <p class="two-factor-setup__label">Correo confirmado. Ahora elige cómo quieres recibir tus códigos:</p>
      <button
        v-for="option in options"
        :key="option.method"
        type="button"
        class="two-factor-setup__option"
        :disabled="loading"
        @click="start(option.method)">
        <feather-icon :icon="option.icon" svgClasses="w-6 h-6" class="two-factor-setup__option-icon"/>
        <span class="two-factor-setup__option-text">
          <strong>{{ option.title }}</strong>
          <small>{{ option.description }}</small>
        </span>
        <span v-if="currentMethod === option.method" class="two-factor-setup__badge">Actual</span>
      </button>
    </div>

    <!-- Paso 3: app autenticadora -->
    <div v-else-if="step === 'TOTP'">
      <ol class="two-factor-setup__steps">
        <li>Abre tu app autenticadora o instala una (Google Authenticator, Microsoft Authenticator o Authy).</li>
        <li>Agrega una cuenta escaneando este código QR.</li>
      </ol>
      <img :src="setup.qr_code" alt="Código QR para agregar Verificamex Firma a tu app autenticadora" class="two-factor-setup__qr">
      <p class="two-factor-setup__muted text-center">¿No puedes escanearlo? Escribe esta clave en la app:</p>
      <div class="two-factor-setup__secret">
        <code>{{ formattedSecret }}</code>
        <a href="#" @click.prevent="copySecret">Copiar</a>
      </div>
      <p class="two-factor-setup__label">Escribe el código de 6 dígitos que muestra la app:</p>
      <two-factor-code-input ref="codeInput" :disabled="loading" @input="code = $event" @complete="confirm"/>
    </div>

    <!-- Paso 3: correo (ya confirmado, no lleva otro código) -->
    <div v-else-if="step === 'EMAIL'">
      <p class="two-factor-setup__label">
        Cada vez que inicies sesión te enviaremos un código a <strong>{{ setup.email_hint }}</strong>.
      </p>
    </div>

    <p v-if="error" class="two-factor-setup__error">{{ error }}</p>

    <template v-if="step === 'TOTP' || step === 'EMAIL'">
      <vs-button
        class="w-full mt-5"
        color="primary"
        type="relief"
        :disabled="loading || (step === 'TOTP' && code.length !== 6)"
        @click="confirm">{{ step === 'EMAIL' ? 'Activar' : 'Confirmar' }}</vs-button>
      <p class="text-center mt-4">
        <a href="#" class="two-factor-setup__link" @click.prevent="backToChoose">Elegir otro método</a>
      </p>
    </template>

    <p v-if="mode === 'settings' && (step === 'verify-email' || step === 'choose')" class="text-center mt-4">
      <a href="#" class="two-factor-setup__link" @click.prevent="$emit('cancel')">Cancelar</a>
    </p>
  </div>
</template>

<script>
import { twoFactorService } from '@/_services';
import TwoFactorCodeInput from './TwoFactorCodeInput';

const RESEND_SECONDS = 60;

// Configurar el método de doble factor: primero se confirma el correo con un código
// (así quien solo tiene la contraseña no puede registrar su propia app), luego se elige
// el método y se confirma (la app con su código; el correo queda activo de inmediato).
// En mode "login" lo usa quien aún no lo tiene configurado (con el two_factor_token) y
// al confirmar emite "completed" con la sesión que devuelve la API. En mode "settings"
// cambia el método con la sesión abierta y emite "completed" con el método nuevo.
export default {
  name: 'TwoFactorMethodSetup',
  components: { TwoFactorCodeInput },
  props: {
    mode: { type: String, default: 'login' },
    token: { type: String, default: null },
    currentMethod: { type: String, default: null },
    emailHint: { type: String, default: null },
    // "Recordar por 30 días" marcado en el login (solo mode "login").
    rememberDevice: { type: Boolean, default: false }
  },
  data() {
    return {
      step: 'verify-email',
      emailCodeSent: false,
      hint: this.emailHint,
      setup: null,
      code: '',
      loading: false,
      error: null,
      resendIn: 0,
      timer: null,
      options: [
        {
          method: 'TOTP',
          icon: 'SmartphoneIcon',
          title: 'App autenticadora',
          description: 'Google Authenticator, Microsoft Authenticator, Authy u otra. Genera los códigos en tu celular, aun sin internet.'
        },
        {
          method: 'EMAIL',
          icon: 'MailIcon',
          title: 'Código por correo',
          description: 'Cada vez que inicies sesión te enviamos un código a tu correo.'
        }
      ]
    }
  },
  computed: {
    formattedSecret() {
      return this.setup && this.setup.secret ? this.setup.secret.match(/.{1,4}/g).join(' ') : '';
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    sendEmailCode() {
      this.loading = true;
      this.error = null;
      const request = this.mode === 'login'
        ? twoFactorService.sendLoginSetupEmailCode(this.token)
        : twoFactorService.sendChangeEmailCode();

      request
        .then(response => {
          this.hint = response.email_hint;
          this.emailCodeSent = true;
          this.code = '';
          if (this.$refs.codeInput) {
            this.$refs.codeInput.clear();
          }
          this.startResendTimer();
        }, this.handleError)
        .finally(() => {
          this.loading = false;
        });
    },
    verifyEmail() {
      if (this.loading || this.code.length !== 6) {
        return;
      }
      this.loading = true;
      this.error = null;
      const request = this.mode === 'login'
        ? twoFactorService.verifyLoginSetupEmail(this.token, this.code)
        : twoFactorService.verifyChangeEmail(this.code);

      request
        .then(() => {
          this.step = 'choose';
          this.code = '';
        }, error => {
          this.handleError(error);
          if (this.$refs.codeInput) {
            this.$refs.codeInput.clear();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    start(method) {
      this.loading = true;
      this.error = null;
      const request = this.mode === 'login'
        ? twoFactorService.startLoginSetup(this.token, method)
        : twoFactorService.startChange(method);

      return request
        .then(setup => {
          this.setup = setup;
          this.step = method;
          this.code = '';
          if (this.$refs.codeInput) {
            this.$refs.codeInput.clear();
          }
        }, this.handleError)
        .finally(() => {
          this.loading = false;
        });
    },
    confirm() {
      if (this.loading || (this.step === 'TOTP' && this.code.length !== 6)) {
        return;
      }
      this.loading = true;
      this.error = null;
      const method = this.step;
      // Con correo no hay código: el correo se acaba de confirmar.
      const code = method === 'TOTP' ? this.code : undefined;
      const request = this.mode === 'login'
        ? twoFactorService.confirmLoginSetup(this.token, code, this.rememberDevice)
        : twoFactorService.confirmChange(code);

      request
        .then(response => {
          this.$emit('completed', this.mode === 'login' ? response : method);
        }, error => {
          this.handleError(error);
          if (this.$refs.codeInput) {
            this.$refs.codeInput.clear();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    backToChoose() {
      this.step = 'choose';
      this.setup = null;
      this.code = '';
      this.error = null;
    },
    copySecret() {
      this.$copyText(this.setup.secret).then(() => {
        this.$vs.notify({ title: 'Clave copiada', text: 'Pégala en tu app autenticadora', color: 'success', iconPack: 'feather', icon: 'icon-check-circle' });
      });
    },
    startResendTimer() {
      this.resendIn = RESEND_SECONDS;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.resendIn--;
        if (this.resendIn <= 0) {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    handleError(error) {
      // En el login, un 401 es que el token de verificación expiró: hay que volver a la contraseña.
      if (this.mode === 'login' && error && error.status === 401) {
        this.$emit('expired', error.message);
        return;
      }
      // 403: el correo no está confirmado o la confirmación venció (30 min). Hay que repetirla.
      if (error && error.status === 403) {
        this.step = 'verify-email';
        this.emailCodeSent = false;
        this.setup = null;
        this.code = '';
      }
      this.error = (error && error.message) || error;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_colorsVariables.scss';

.two-factor-setup__option {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 14px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover:not(:disabled),
  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.two-factor-setup__option-icon {
  color: $primary;
  flex-shrink: 0;
}

.two-factor-setup__option-text {
  display: flex;
  flex-direction: column;
  flex: 1;

  strong {
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.85);
  }
  small {
    margin-top: 2px;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.4;
  }
}

.two-factor-setup__badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: $primary;
  background: rgba(115, 103, 240, 0.12);
  border-radius: 999px;
  padding: 2px 10px;
}

.two-factor-setup__steps {
  padding-left: 18px;
  margin-bottom: 12px;
  font-size: 0.92rem;
  color: rgba(0, 0, 0, 0.75);
  list-style: decimal;

  li { margin-bottom: 4px; }
}

.two-factor-setup__qr {
  display: block;
  width: 180px;
  height: 180px;
  margin: 0 auto 8px;
}

.two-factor-setup__secret {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;

  code {
    font-size: 0.9rem;
    letter-spacing: 1px;
    padding: 4px 8px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.05);
    word-break: break-all;
  }
  a {
    font-size: 0.85rem;
    color: $primary;
    font-weight: 500;
  }
}

.two-factor-setup__label {
  font-size: 0.92rem;
  color: rgba(0, 0, 0, 0.75);
  margin-bottom: 12px;
}

.two-factor-setup__muted {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);

  a { color: $primary; font-weight: 500; }
}

.two-factor-setup__error {
  margin-top: 12px;
  text-align: center;
  font-size: 0.9rem;
  color: $danger;
}

.two-factor-setup__link {
  font-size: 0.9rem;
  color: $primary;
  font-weight: 500;
}
</style>
